import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Card } from 'sutra/components/Card/Card'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

import { AgentNameTitle } from 'features/trend-detection/features/agent-create/components/AgentNameTitle'
import { AgentTypeField } from 'features/trend-detection/features/agent-create/components/AgentTypeField'
import { SubmitBlockers } from 'features/trend-detection/features/agent-create/components/SubmitBlockers'
import { TriggerSection } from 'features/trend-detection/features/agent-create/components/TriggerSection'
import type { AgentType } from 'features/trend-detection/types'

export interface AgentCreateFormHeaderProps {
  name: string
  onNameChange: (value: string) => void
  namePlaceholder: string
  agentType: AgentType
  onAgentTypeChange: (type: AgentType) => void
  availableAgentTypes: AgentType[]
  /** True in edit mode — an agent's type isn't mutable after creation. */
  typeDisabled?: boolean
}

export interface AgentCreateFormSubmitProps {
  label: string
  disabled: boolean
  /** Unmet requirements, shown above the button once the user has moved
   * past the name field. Distinct from `disabled`, which also covers
   * silent loading states with nothing to tell the user. */
  blockers: string[]
  submitting: boolean
  /** Shown in place of `label` while submitting. Defaults to "Creating…"
   * (the create form's original text) so existing callers are unaffected;
   * the edit-mode Container passes "Saving…". */
  submittingLabel?: string
  onSubmit: () => void
}

export interface AgentCreateFormProps {
  header: AgentCreateFormHeaderProps
  /** The per-type Instructions body — `IssueAgentFields` /
   * `BenchmarkingAgentFields` / `ReportsAgentFields`, picked by the
   * Container based on `header.agentType`. This is the seam that keeps the
   * shell itself agent-type-agnostic. */
  instructions: React.ReactNode
  /** Omitted entirely for a type with no notification preference (Reports —
   * see the concept matrix) rather than rendered disabled. */
  permissions?: React.ReactNode
  /** The Sharing card's body — omitted on create (nothing to share before
   * the agent exists) and present only in edit mode. */
  sharing?: React.ReactNode
  submit: AgentCreateFormSubmitProps
  onCancel: () => void
}

/** The unified single-screen agent create form — replaces the old
 * type → configure → review wizard. No steps, no step indicator: every
 * field lives on one scrollable 640px-wide page. */
export const AgentCreateForm: React.FC<AgentCreateFormProps> = ({
  header,
  instructions,
  permissions,
  sharing,
  submit,
  onCancel,
}) => {
  // The name field is autofocused, so its first blur means the user has
  // moved on — that is when the blockers list and the name's error border
  // appear, rather than on a pristine form.
  const [touched, setTouched] = useState(false)
  const showBlockers =
    touched && submit.blockers.length > 0 && !submit.submitting

  return (
    <Page>
      <Section>
        <SectionTitle>Name and type</SectionTitle>
        <HeaderRow>
          <NameBlock>
            <AgentNameTitle
              value={header.name}
              onChange={header.onNameChange}
              onBlur={() => setTouched(true)}
              placeholder={header.namePlaceholder}
              error={touched && !header.name.trim()}
            />
          </NameBlock>
          <TypeFieldWrap>
            <AgentTypeField
              value={header.agentType}
              onChange={header.onAgentTypeChange}
              availableTypes={header.availableAgentTypes}
              disabled={header.typeDisabled}
            />
          </TypeFieldWrap>
        </HeaderRow>
      </Section>

      <Section>
        <SectionTitle>Trigger</SectionTitle>
        <Card>
          <TriggerSection agentType={header.agentType} />
        </Card>
      </Section>

      <Section>
        <SectionTitle>Instructions</SectionTitle>
        <Card>{instructions}</Card>
      </Section>

      {permissions && (
        <Section>
          <SectionTitle>Permissions</SectionTitle>
          <Card>{permissions}</Card>
        </Section>
      )}

      {sharing && (
        <Section>
          <SectionTitle>Sharing</SectionTitle>
          <Card>{sharing}</Card>
        </Section>
      )}

      <SubmitArea>
        {showBlockers && <SubmitBlockers blockers={submit.blockers} />}
        <SubmitRow>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit.onSubmit}
            disabled={submit.disabled || submit.submitting}
          >
            {submit.submitting
              ? (submit.submittingLabel ?? 'Creating…')
              : submit.label}
          </Button>
        </SubmitRow>
      </SubmitArea>
    </Page>
  )
}

const Page = styled.div`
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing[8]}px;
  padding: ${spacing[8]}px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]}px;
`

const NameBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
`

// Sutra's Select's trigger is `width: 100%`, sized against its containing
// block — same wrap-in-a-sizing-div pattern charts-v2's numeric filter pane
// uses for the same component (Select forwards no className either, so it
// can't be `styled(Select)` directly).
const TypeFieldWrap = styled.div`
  width: fit-content;
  flex-shrink: 0;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.md.fontSize}px;
  line-height: ${typography.text.md.lineHeight}px;
  font-weight: ${typography.weights.semibold};
  color: ${gray[900]};
`

const SubmitArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
`

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[2]}px;
`
